/**
 * ChartAnnotation - Floating annotation overlay for charts (Narrative Shell).
 *
 * Renders an absolutely-positioned label above a chart point (x, y) with a
 * small downward triangle pointer. Color is one of 'good' | 'bad' | 'ice'.
 * The label fades and slides smoothly as the target point moves.
 *
 * 图表注释浮层：在图表点 (x, y) 上方渲染绝对定位标签，底部带向下小三角指针。
 * 颜色取 'good' | 'bad' | 'ice' 之一；目标点移动时标签平滑淡入/滑动。
 */
import type { CSSProperties } from 'react';

// ----- Types -----

export interface ChartAnnotationProps {
  /** Anchor x (px). / 锚点 x（像素）。 */
  x: number;
  /** Anchor y (px). / 锚点 y（像素）。 */
  y: number;
  /** Label text. / 标签文本。 */
  text: string;
  /** Label color theme. / 标签配色。 */
  color: 'good' | 'bad' | 'ice';
  /** Whether the label is shown. / 是否显示。 */
  visible: boolean;
}

// ----- Color mapping -----

const COLOR_MAP: Record<ChartAnnotationProps['color'], string> = {
  good: 'var(--status-good)',
  bad: 'var(--status-bad)',
  ice: 'var(--color-ice)',
};

// ----- Component -----

export default function ChartAnnotation({
  x,
  y,
  text,
  color,
  visible,
}: ChartAnnotationProps) {
  const colorVar = COLOR_MAP[color];

  // Positioned above the anchor (translate(-50%, -120%) lifts it off the point).
  // 定位于锚点上方（translate(-50%, -120%) 将标签抬离锚点）。
  const labelStyle: CSSProperties = {
    position: 'absolute',
    left: x,
    top: y,
    transform: 'translate(-50%, -120%)',
    background: 'rgba(22, 22, 31, 0.85)',
    color: colorVar,
    fontSize: 'var(--fs-xs)',
    padding: 'var(--space-xs) var(--space-sm)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-default)',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    opacity: visible ? 1 : 0,
    transition:
      'left 250ms ease-in-out, top 250ms ease-in-out, opacity 250ms ease-in-out',
  };

  // Downward triangle pointer at the bottom center of the label.
  // 标签底部居中的向下三角指针。
  const triangleStyle: CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 0,
    height: 0,
    borderLeft: '5px solid transparent',
    borderRight: '5px solid transparent',
    borderTop: `6px solid ${colorVar}`,
  };

  return (
    <div style={labelStyle}>
      {text}
      <span style={triangleStyle} />
    </div>
  );
}
